import React from "react";
import styled, { css } from "styled-components";

import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

type Direction = "left" | "right";

interface ShadowScrollProps {
  direction: Direction;
  tableRef: React.MutableRefObject<HTMLDivElement | null>;
  right?: number
}

/** Component that renders over a table to display a shadow that is hoverable to trigger the horizontal scroll */
export const ShadowScroll: React.FC<ShadowScrollProps> = ({
  direction, tableRef, right
}) => {
  const scrollIntervalRef = React.useRef(0);
  const [ hover, setHover ] = React.useState<boolean>(false);

  /** scrolls the table when hovering */
  const handleScroll = React.useCallback(() => {
    let scrollValue: number;

    if (direction === "right") {
      scrollValue = (tableRef.current as HTMLDivElement).scrollLeft + 60;
    } else {
      scrollValue = (tableRef.current as HTMLDivElement).scrollLeft - 60;
    }

    tableRef.current?.scroll({
      left: scrollValue,
      behavior: "smooth"
    });

    // once 0 clear the interval
    if (scrollValue <= 0 || scrollValue + (tableRef.current as HTMLDivElement).clientWidth >= (tableRef.current as HTMLDivElement).scrollWidth) {
      clearInterval(scrollIntervalRef.current);
      setHover(false);
    }
  }, [ direction, tableRef ]);

  const handleHover = React.useCallback(() => {
    clearInterval(scrollIntervalRef.current);
    handleScroll();
    setHover(true);
    scrollIntervalRef.current = setInterval(() => handleScroll(), 100) as unknown as number;
  }, [ handleScroll ]);

  const handleUnhover = React.useCallback(() => {
    clearInterval(scrollIntervalRef.current);
    setHover(false);
  }, []);

  return (
    <ShadowScrollWrapper
      hover={hover}
      direction={direction}
      right={right}
    >
      <div className="shadow" />

      <div
        className="scroll"
        onMouseOver={handleHover}
        onMouseOut={handleUnhover}
      >
        <div className="icon">
          <Icon
            icon={direction === "left" ? Icons.CHEVRONLEFT : Icons.CHEVRONRIGHT}
            size={35}
            $fillColor="primaryDark"
          />
        </div>

      </div>
    </ShadowScrollWrapper>
  );
};

const ShadowScrollWrapper = styled.div<{hover: boolean, direction: Direction, right?: number}>`${({
  hover, direction, right, theme
}) => css`{
  @media (hover: none) and (pointer: coarse) {
    display: none;
  }

  position: absolute;
  height: 100%;
  z-index: 1;
  ${right !== undefined && `right: ${right}px;`}

  .shadow {
    position: absolute;
    height: 100%;
    box-shadow: ${hover ? `0 0 20px 5px ${theme.colors.primaryDark}` : `0 0 12px 2px ${theme.colors.primaryDark}`};
    clip-path: ${direction === "left" ? "inset(0px -32px 0px 0px)" : "inset(0px 0px 0px -32px)"};
    transition: 0.2s box-shadow ease-in-out;
  }

  .scroll {
    position: absolute;
    ${direction === "right" && "right: 0px;"}
    height: 100%;
    display: flex;
    width: 35px;
    justify-content: center;
    align-items: center;

    :hover {
      cursor: ${direction === "left" ? "w-resize" : "e-resize"};
    }
  }

  .icon {
    opacity: ${hover ? 0.7 : 0};
    transition: 0.2s opacity ease-in-out;
  }
}`}`;
